// Authentication types
// UserRole is derived from constants/roles.ts — add new roles there
import type { UserRole } from '../constants/roles'

export type LoginRequest = {
  email: string
  password: string
}

export type LoginResponse = {
  user: User
  accessToken: string
  refreshToken: string
}

export type RefreshTokenRequest = {
  refreshToken: string
}

export type RefreshTokenResponse = {
  accessToken: string
}

export type CreateDoctorRequest = {
  email: string
  password: string
  firstName: string
  lastName: string
  dateOfBirth: string
}

export type ResetPasswordRequest = {
  userId: string
  newPassword: string
}

export type GdprDeletionPreview = {
  userId: string
  userEmail: string
  firstName: string | null
  lastName: string | null
  profilesCount: number
  ordersCount: number
  aiSessionsCount: number
  aiResponsesCount: number
  aiQcReviewsCount: number
  eventLogsCount: number
  emailAuthCodesCount: number
  totalRecordsCount: number
}

export type GdprDeletionSummary = {
  userId: string
  userEmail: string
  profilesDeleted: number
  ordersDeleted: number
  aiSessionsDeleted: number
  aiResponsesDeleted: number
  aiQcReviewsDeleted: number
  eventLogsDeleted: number
  emailAuthCodesDeleted: number
  deletedAt: string
}

export type GdprUserExport = {
  user: {
    id: string
    email: string
    firstName: string | null
    lastName: string | null
    dateOfBirth: string | null
    isVerified: boolean
    countryCode: string
    createdAt: string
    updatedAt: string
  }
  profiles: Array<{
    id: string
    age: string | null
    sex: string | null
    weight: string | null
    height: string | null
    bloodPressureSystolic: string | null
    bloodPressureDiastolic: string | null
    healthGoals: string[] | null
    healthConcerns: string[] | null
    medications: string | null
    supplements: string | null
    familyHistory: string | null
    activeSymptoms: string | null
    previousLabs: string | null
    testsOfInterest: string | null
    insurance: string | null
    smoking: string | null
    focusType: string | null
    dynamicQuestions: Array<{ q: string; a: string; answeredAt: string }> | null
    profileVersion: number
    onboardingCompleted: boolean
    createdAt: string
    updatedAt: string
  }>
  orders: Array<{
    id: string
    junctionOrderId: string | null
    packageId: string | null
    price: number | null
    currency: string | null
    status: string
    pscAppointmentStatus: string | null
    pscAppointmentDate: string | null
    pscAppointmentLocation: string | null
    createdAt: string
    updatedAt: string
  }>
  aiSessions: Array<{
    id: string
    sessionType: string
    createdAt: string
    responses: Array<{
      id: string
      messageType: string
      content: unknown
      createdAt: string
    }>
  }>
  exportMetadata: {
    exportedAt: string
    exportedBy: string
    totalRecords: number
  }
}
export type { UserRole }

export type UserSource = 'direct' | 'marketing' | 'app'

export enum UserType {
  REGULAR = 0,
  CONTENT_ONLY = 1,
}

export type User = {
  id: string
  email: string
  firstName: string | null
  lastName: string | null
  dateOfBirth: string | null
  role: UserRole
  isVerified: boolean
  userType: UserType
  userSource: UserSource
  externalProfileId: string | null
  countryCode: string
  createdAt: string
  updatedAt: string
  ordersCount?: number
}

export type UsersStats = {
  verified: number
  purchased: number
}

export type UsersResponse = {
  users: User[]
  total: number
  page: number
  size: number
  stats: UsersStats
}

export type UsersExportResponse = {
  items: User[]
  nextCursor: string | null
}

export type AdNameLookupItem = {
  email: string
  found: boolean
  userId: string | null
  adName: string | null
  adsetId: string | null
  purchasedAt: string | null
}

export type AdNamesLookupResponse = {
  items: AdNameLookupItem[]
}

// User analytics (server-side aggregation)
export type UserTypeStats = {
  total: number
  verifiedPurchased: number
  verifiedNotPurchased: number
  unverifiedPurchased: number
  unverifiedNotPurchased: number
}

export type UserAnalytics = {
  stats: {
    regular: UserTypeStats
    contentOnly: UserTypeStats
    overall: UserTypeStats
  }
  dailySignups: Array<{ date: string; total: number; verified: number }>
  countryBreakdown: Array<{ country: string; count: number }>
}

export type UpdateUserRequest = {
  email?: string | undefined
  firstName?: string | undefined
  lastName?: string | undefined
  role?: UserRole | undefined
  isVerified?: boolean | undefined
  userType?: UserType | undefined
  userSource?: UserSource | undefined
  externalProfileId?: string | undefined
  countryCode?: string | undefined
}

export type ApiResponse<T> = {
  success: boolean
  data: T
  message?: string
}

export type ApiError = {
  success: false
  message: string
  error?: string
  statusCode?: number
}

// Orders types
export const ORDER_STATUSES = [
  'purchased',
  'created',
  'received',
  'in_progress',
  'unknown',
  'completed',
  'failed',
  'cancelled',
  'deleted',
] as const

export type OrderStatus = (typeof ORDER_STATUSES)[number]

export type Order = {
  id: string
  junctionOrderId: string | null
  packageId: string | null
  price: number | null
  currency: string | null
  userId: string
  user: User
  status: OrderStatus
  pscAppointmentStatus: string | null
  webhookPayload: Record<string, unknown> | null
  createdAt: string
  updatedAt: string
}

export type JunctionRelinkCandidate = {
  junctionOrderId: string
  status: string
  createdAt: string
  labTestName: string
  linkedToOrderId: string | null
}

export type JunctionRelinkCandidatesResponse = {
  candidates: JunctionRelinkCandidate[]
}

// User-facing metadata (minimal)
export type LabRecommendationsUserMetadata = {
  generatedAt?: string
  profileVersion?: number
}

// Admin-facing metadata (full diagnostic info)
export type LabRecommendationsAdminMetadata = {
  modelUsed: string
  tokensUsed?: number
  processingTimeMs?: number
  profileVersion: number
  prompt?: string
  promptHash?: string
  responseType?: string
  status?: string
}

// User-facing recommendations (clean, focused)
export type LabRecommendationsUserResponse = {
  hasRecommendations: boolean
  message: string
  recommendations?: unknown
  metadata?: LabRecommendationsUserMetadata
}

// Admin-facing recommendations (full diagnostic info)
export type LabRecommendationsAdminResponse = {
  hasRecommendations: boolean
  message: string
  recommendations?: unknown
  generatedAt?: string
  metadata?: LabRecommendationsAdminMetadata
}

export type PurchasedPackageResponse = {
  id: string
  packageId: string
  price?: number
  currency?: string
  status: string
  createdAt: string
  updatedAt: string
}

export type PurchasedPackage = {
  name: string
  junctionName?: string
  labTestId: string
  labProvider: string
  isContentOnly?: boolean
}

// Paginated orders response
export type PaginatedOrdersResponse = {
  items: Order[]
  total: number
}

// Order analytics (server-side aggregation)
export type OrderStatusCount = {
  status: string
  count: number
}

export type MonthlyRevenueEntry = {
  month: string
  revenue: number
  count: number
}

export type PackageCount = {
  packageId: string | null
  count: number
}

export type OrderAnalytics = {
  statusCounts: OrderStatusCount[]
  monthlyRevenue: MonthlyRevenueEntry[]
  packageCounts: PackageCount[]
  totalRevenue: number
  paidOrdersCount: number
  totalOrdersCount: number
}

// Orders query params
export type GetOrdersParams = {
  page?: number | undefined
  limit?: number | undefined
  status?: OrderStatus | undefined
  packageIds?: string | undefined
  dateFrom?: string | undefined
  dateTo?: string | undefined
  search?: string | undefined
}

// Create Order types
export type CreateOrderRequest = {
  email: string
  packageId: string
  price: number
  currency: string
}

// Auth Code types
export type CreateAuthCodeRequest = {
  email: string
}

export type AuthCodeResponse = {
  code: string
  expiresAt: string
}

// AI QC types
export type QCStatus = 'pending' | 'approved' | 'rejected'

export type AIQCReview = {
  id: string
  aiResponseId: string
  sessionId: string
  status: QCStatus
  reviewedByUserId: string | null
  reviewedBy: User | null
  session: {
    id: string
    user: User
    metadata?: {
      labOrderId?: string
      junctionLabOrderId?: string
      triggeredBy?: string
      [key: string]: unknown
    }
  }
  createdAt: string
  updatedAt: string
}

// AI QC Response type (for reviewing AI responses)
export type AIQCResponse = {
  hasRecommendations: boolean
  message: string
  recommendations: unknown
  generatedAt?: string
  metadata?: {
    modelUsed: string
    tokensUsed?: number
    processingTimeMs?: number
    promptHash: string
    responseType: string
    status: string
    prompt?: string
  }
}

// Unhealthy Customers types
export type UnhealthyCustomerEntry = {
  userId: string
  email: string
  firstName: string | null
  lastName: string | null
  orderId: string
  orderStatus: string
  packageId: string | null
  orderCreatedAt: string
  orderUpdatedAt: string
  junctionOrderId: string | null
  detail: Record<string, unknown>
}

export type UnhealthyCategory = {
  id: string
  label: string
  description: string
  customers: UnhealthyCustomerEntry[]
}

export type UnhealthyCustomersResponse = {
  categories: UnhealthyCategory[]
  generatedAt: string
}

// Pipeline SLA types
export type SlaStatus = 'on_track' | 'approaching' | 'breaching'

export type SlaOrderEntry = {
  orderId: string
  userId: string
  email: string
  firstName: string | null
  lastName: string | null
  orderStatus: string
  packageId: string | null
  stageEnteredAt: string
  hoursInStage: number
  slaHours: number
  slaStatus: SlaStatus
}

export type PipelineStage = {
  id: string
  label: string
  description: string
  slaHours: number
  breachingCount: number
  approachingCount: number
  onTrackCount: number
  orders: SlaOrderEntry[]
}

export type WeeklyMetric = {
  weekStart: string
  medianHours: number
  p95Hours: number
  completedCount: number
}

export type StageMetrics = {
  stageId: string
  label: string
  medianHours: number | null
  p95Hours: number | null
  averageHours: number | null
  completedCount: number
  weeklyTrend: WeeklyMetric[]
}

export type PipelineSlaResponse = {
  stages: PipelineStage[]
  metrics: StageMetrics[]
  generatedAt: string
}

// Event Log types
export type EventLogLevel = 'info' | 'warning' | 'error'

export type ActorType = 'system' | 'user' | 'webhook' | 'admin' | 'doctor'

export type EventLog = {
  id: string
  createdAt: string
  level: EventLogLevel
  eventType: string
  message: string
  actorType: ActorType
  actorId: string | null
  userId: string | null
  user: User | null
  orderId: string | null
  order: Order | null
  metadata: Record<string, unknown> | null
}
